import { useTheme } from "app/shared";
import { FC, useMemo } from "react";
import Analysis from "./Analysis/Analysis";
import Backers from "./Backers/Backers";
import CompetitiveEdge from "./CompetitiveEdge/CompetitiveEdge";
import Footer from "./Footer/Footer";
import ProblemSolution from "./ProblemSolution/ProblemSolution";
import ProjectMemoHeader from "./ProjectMemoHeader/ProjectMemoHeader";
import RisksOpportunities from "./RisksOpportunities/RisksOpportunities";
import TeamSection from "./TeamSection/TeamSection";
import Traction from "./Traction/Traction";

import styles from "./ProjectMemo.module.scss";

interface KeyValueItem {
  label: string;
  value: string;
}

interface OpportunityRisk {
  opportunity: string;
  risk: string;
}

interface ParsedTeamMember {
  name: string;
  position: string;
  description: string;
  photoUrl?: string;
}

interface ProjectMemoData {
  memoHeader?: KeyValueItem[];
  description?: KeyValueItem[];
  team?: KeyValueItem[];
  teamPhoto?: KeyValueItem[];
  problemSolution?: KeyValueItem[];
  tokenInfo?: KeyValueItem[];
  tokenUtility?: KeyValueItem[];
  traction?: KeyValueItem[];
  backers?: KeyValueItem[];
  competitiveEdge?: KeyValueItem[];
  analysis?: KeyValueItem[];
  opportunitiesRisks?: KeyValueItem[];
}

type ProjectMemoProps = {
  data: Partial<ProjectMemoData>[];
};

const ProjectMemo: FC<ProjectMemoProps> = ({ data }) => {
  const { theme } = useTheme();

  const getSection = <K extends keyof ProjectMemoData>(
    key: K
  ): KeyValueItem[] => {
    const section = data.find((entry) => entry[key])?.[key];
    return Array.isArray(section) ? section : [];
  };

  const header = getSection("memoHeader");
  const description = getSection("description")[0]?.value || "";
  const teamRaw = getSection("team");
  const photosRaw = getSection("teamPhoto");
  const problemItems = getSection("problemSolution");
  const traction = getSection("traction");
  const backers = getSection("backers").map((item) => item.value);
  const competitiveEdge = getSection("competitiveEdge").map(
    (item) => item.value
  );
  const analysis = getSection("analysis")[0]?.value || "";
  const opportunitiesRisksRaw = getSection("opportunitiesRisks");
  const [problemObj, solutionObj] = problemItems;
  const problem = problemObj?.value ?? "";
  const solution = solutionObj?.value ?? "";
  const values = header.map((item) => item.value || "");
  const [name, vertical, followers, pitchDeck, tokenomics, website] = values;

  const team: ParsedTeamMember[] = useMemo(
    () =>
      teamRaw.map((member, idx) => {
        const [name, ...descParts] = member.value.split(" — ");
        return {
          name: name.trim(),
          position: member.label.trim(),
          description: descParts.join(" — ").trim(),
          photoUrl: photosRaw[idx]?.value,
        };
      }),
    [teamRaw, photosRaw]
  );

  const opportunitiesRisks: OpportunityRisk[] = opportunitiesRisksRaw.reduce<
    OpportunityRisk[]
  >((acc, curr) => {
    if (curr.label.toLowerCase().includes("opportunity")) {
      acc.push({ opportunity: curr.value, risk: "" });
    } else if (curr.label.toLowerCase().includes("risk")) {
      if (acc.length === 0 || acc[acc.length - 1].risk) {
        acc.push({ opportunity: "", risk: curr.value });
      } else {
        acc[acc.length - 1].risk = curr.value;
      }
    }
    return acc;
  }, []);

  return (
    <div className={styles.memoContainer}>
      <div className={styles.header}>
        <h1>Project Memo</h1>
      </div>
      <ProjectMemoHeader
        pitchDeck={pitchDeck}
        website={website}
        tokenomics={tokenomics}
        name={name}
        vertical={vertical}
        followers={followers}
      />
      <div
        className={`${styles.section} ${
          theme === "dark" ? styles.darkTheme : ""
        }`}
      >
        <h2>What they are building</h2>
        <p>{description}</p>
      </div>
      <TeamSection team={team} />
      <ProblemSolution problem={problem} solution={solution} />
      <Traction data={traction} />
      <Backers backers={backers} />
      <CompetitiveEdge edges={competitiveEdge} />
      <Analysis text={analysis} />
      <RisksOpportunities items={opportunitiesRisks} />
      <Footer />
    </div>
  );
};

export default ProjectMemo;
